import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { CustomerSessionService } from './customer-session.service';
import { environment } from '../../../environments/environment';

/** Header carrying the opaque customer session token (matches backend CustomerContextAccessor.HeaderName). */
export const CUSTOMER_TOKEN_HEADER = 'X-Customer-Token';

/**
 * D4A — attaches `X-Customer-Token` ONLY to the customer + delivery-order
 * endpoints, and only when a valid (non-expired) session exists. Never sent
 * to menu / table-access / static-asset requests, so a guest token can't leak
 * onto unrelated calls.
 *
 * Eligible URLs (under the configured API base):
 *   • /api/public/customer/...           (profile + addresses; D2)
 *   • /api/public/store/{token}/delivery/...  (order placement; D3A)
 */
export const customerTokenInterceptor: HttpInterceptorFn = (req, next) => {
  if (!isCustomerScopedUrl(req.url)) return next(req);

  const session = inject(CustomerSessionService);
  const token = session.currentToken();
  if (!token) return next(req);

  return next(req.clone({ setHeaders: { [CUSTOMER_TOKEN_HEADER]: token } }));
};

export function isCustomerScopedUrl(url: string): boolean {
  // Normalise away the configured base so dev (localhost:5213/api) and prod
  // (relative /api) both match on the same path shape.
  let path = url;
  const base = environment.apiUrl;
  if (base && path.startsWith(base)) path = path.slice(base.length);
  // Tolerate absolute origins we didn't strip (defensive).
  const idx = path.indexOf('/public/');
  const tail = idx >= 0 ? path.slice(idx) : path;

  if (tail.startsWith('/public/customer/') || tail === '/public/customer') return true;
  // /public/store/{token}/delivery...
  return /^\/public\/store\/[^/]+\/delivery(\/|$)/.test(tail);
};
